import type { SearchEntityType, SearchSortMode } from './entity-types';

/**
 * Column model for the search-results TABLE layout.
 *
 * Every section on `/app/search` renders as a real table: a sticky column
 * header row (`SearchTableHeader`) over aligned cells (`SearchTableRow`).
 * Columns are per-entity — a Screens section ends at Last online, a Members
 * section shows Role / Email / Team — because a single unified column set would
 * have to flatten roles, tags and locations into one generic "Kind" column and
 * lose them.
 *
 * The header and the row BOTH read this spec, so a width or breakpoint only
 * ever exists in one place. That's the whole point of the file: the Screens
 * and Assets pages keep their column widths in sync by hand across three
 * files (`ScreenListColumnHeader` / `ScreenRow` / `ScreenFolderRow`) and a
 * mismatch there is a recurring alignment bug (#1897). Don't reintroduce
 * that pattern here — add the column to `COLUMN_CLASS` and let both
 * consumers pick it up.
 */

/** Trailing columns. The Name column is implicit — always first, always
 *  present, and structurally different (thumbnail slot + highlight +
 *  shared-folder glyph), so it's hardcoded by both consumers instead of
 *  living in this union. */
export type SearchColumnId =
  | 'status'
  | 'tags'
  | 'location'
  | 'workspace'
  | 'recency'
  | 'role'
  | 'email'
  | 'team'
  | 'members';

export interface SearchColumn {
  id: SearchColumnId;
  /** Untranslated label — consumers wrap in `t()`. */
  labelKey: string;
  /**
   * Sort mode this column header activates, or `null` for an inert label.
   * Only Name and the recency column map to a sort mode: those are the two
   * the client-side `sortSearchItems` can actually order by. Status / tags /
   * location have no comparator, so their headers stay plain text rather
   * than offering a click that does nothing.
   */
  sort: SearchSortMode | null;
}

/**
 * Width + responsive visibility per column, shared by header and row.
 *
 * Breakpoint ladder is widest-value-drops-first, so the columns that carry
 * the most identity survive longest on a narrow window:
 *   sm  status / recency / role
 *   md  location / team / members
 *   lg  tags / email
 * Name is always visible and absorbs the leftover width (`flex-1`).
 */
export const COLUMN_CLASS: Record<SearchColumnId, string> = {
  status: 'w-24 shrink-0 hidden sm:flex',
  tags: 'w-44 shrink-0 hidden lg:flex',
  location: 'w-40 shrink-0 hidden md:flex',
  // Same width + breakpoint as `location`, which it sits beside: the two answer the same kind of
  // question ("where does this live"), so a narrower teamspace column would read as less important
  // while being the thing that disambiguates two identically-named rows in All view.
  workspace: 'w-40 shrink-0 hidden md:flex',
  recency: 'w-32 shrink-0 hidden sm:flex',
  role: 'w-36 shrink-0 hidden sm:flex',
  email: 'w-56 shrink-0 hidden lg:flex',
  team: 'w-40 shrink-0 hidden md:flex',
  members: 'w-24 shrink-0 hidden md:flex',
};

/**
 * Shared row geometry. The header row and every data row MUST use the same
 * gap and the same leading-slot width, or the columns drift apart.
 *
 * Horizontal inset works out to 20px on both: the header applies `px-5`
 * directly, while a data row is `px-2` on its wrapper (so the hover
 * highlight keeps a gutter from the card edge / scrollbar) plus `px-3`
 * inside the button.
 */
export const TABLE_GAP_CLASS = 'gap-4';
/**
 * Horizontal inset for every row in a section — title, column header, data row.
 * One constant so the three can't drift apart by a pixel.
 */
export const ROW_INSET_CLASS = 'px-5';
/**
 * The hairline under a row, drawn as an inset pseudo-element rather than
 * `border-b`.
 *
 * A border spans the element's FULL width, so on a row padded by
 * `ROW_INSET_CLASS` the rule overshot the text by 20px at each end — the line
 * looked like it was hanging off the table while the text sat tucked inside it.
 * `inset-x-4` is 16px — deliberately 4px SHY of the 20px text inset, so the rule
 * extends a hair past the first character of "Name" and a hair past the last
 * column rather than stopping dead on them. Flush was accurate but read as
 * cramped; 4px frames the row without hanging off it. `ROW_HOVER_CLASS` uses the
 * same inset — change both together or the highlight and the rule disagree.
 */
/**
 * Row hover background, at the SAME inset as `ROW_HAIRLINE_CLASS`.
 *
 * `hover:bg-muted` on the row painted the element's full width, so the
 * highlight overhung the rules once those were inset — the band stuck out past
 * the table it belonged to. Drawn as a `::before` layer at the rule's own
 * `inset-x-4` instead, with `isolate` + `-z-10` so it sits behind the row's own
 * content but still inside the row's stacking context (without `isolate` a
 * negative z-index escapes behind the card and the highlight vanishes).
 */
export const ROW_HOVER_CLASS =
  "relative isolate before:absolute before:inset-x-4 before:inset-y-0 before:-z-10 before:transition-colors before:content-[''] hover:before:bg-muted";

export const ROW_HAIRLINE_CLASS =
  "relative after:absolute after:inset-x-4 after:bottom-0 after:h-px after:bg-muted-foreground/20 after:content-['']";
/** Thumbnail slot at the head of a row — only for the types in
 *  `ENTITY_HAS_LEADING_VISUAL`. */
export const LEADING_SLOT_CLASS = 'w-10 shrink-0';

/**
 * Which entity types earn the 40x40 leading slot.
 *
 * ONLY real imagery earns it — an asset, engage or feed thumbnail. Everything
 * else was filling a 40px picture frame with something that isn't a picture:
 * playlists put a flat colour swatch there (the colour now rides beside the
 * name as a small dot, which is all it ever needed to be) and folders put the
 * generic folder glyph, repeating what the section title directly above the
 * table already says while costing every row 56px of indent. Those rows drop
 * the slot and start at the name.
 *
 * Alignment is per-section (each section is its own table with its own column
 * header), so types can differ here without anything drifting.
 */
export const ENTITY_HAS_LEADING_VISUAL: Record<SearchEntityType, boolean> = {
  assets: true,
  engages: true,
  feeds: true,
  playlists: false,
  folders: false,
  screens: false,
  schedules: false,
  operationalSchedules: false,
  teams: false,
  members: false,
};

// ── Column definitions ───────────────────────────────────────────────────
// Declared once and reused across entities so two sections that show the
// same column can never disagree about its label or sortability.

const STATUS: SearchColumn = { id: 'status', labelKey: 'Status', sort: null };
const TAGS: SearchColumn = { id: 'tags', labelKey: 'Tags', sort: null };
const LOCATION: SearchColumn = { id: 'location', labelKey: 'Location', sort: null };
const ROLE: SearchColumn = { id: 'role', labelKey: 'Role', sort: null };
const EMAIL: SearchColumn = { id: 'email', labelKey: 'Email', sort: null };
const TEAM: SearchColumn = { id: 'team', labelKey: 'Team', sort: null };
const MEMBERS: SearchColumn = { id: 'members', labelKey: 'Members', sort: null };

/** Screens key their recency on `lastHeartBeat` — real device liveness, so
 *  the header says "Last online" (see `getRecencyTimestamp`). */
const LAST_ONLINE: SearchColumn = { id: 'recency', labelKey: 'Last online', sort: 'lastModified' };
/**
 * Owning teamspace — All view only (Group Security §1c.2).
 *
 * `sort: null`, deliberately. The list surfaces offer a client-side workspace sort, which re-orders
 * only the rows already loaded; on this page each section holds 5 rows (All view) or one 50-row
 * offset page, so a clickable header would order that page while looking like it ordered the whole
 * result set. The server has no teamspace-name sort field, so an honest sort is a backend change.
 */
const WORKSPACE: SearchColumn = {
  id: 'workspace',
  labelKey: 'Teamspace',
  sort: null,
};

/** Everything else keys on `lastUpdatedDate` — an authoring signal. */
const LAST_UPDATED: SearchColumn = {
  id: 'recency',
  labelKey: 'Last updated',
  sort: 'lastModified',
};

/**
 * Trailing columns per entity type, left to right.
 *
 * This is a SEARCH result, not the entity's own management page: the job is to
 * identify a hit and get you to it, so each type carries only what distinguishes
 * one result from another. Deliberately dropped, and why:
 *   - screens: OS / Content — device operations, not identity, and Screens
 *     itself is one click away. Status STAYS: online/offline is the first thing
 *     you want to know about a screen you just found, and unlike OS or assigned
 *     content it changes on its own. The condition pills (On Hold, Flash
 *     Message, …) ride beside the name rather than in this column.
 *   - assets / engages / teams / folders: Type — repeats the section it was
 *     found under.
 *   - playlists: Duration — a property you tune while editing, never how you
 *     recognise a playlist in a list.
 *   - schedules / operationalSchedules / playlists: Tags — `updateObjectTags`
 *     is wired for assets ONLY, and neither the Playlists nor the Schedules
 *     feature has any other tag-editing path, so nothing in the product can
 *     put a tag on one. The column was structurally empty: every row printed
 *     an em-dash. (`tags` DOES exist on these documents — it's inherited from
 *     `BaseModel` and indexed for search — which is why the column looked
 *     justified. Being searchable isn't the same as being settable.)
 *     Screens keep theirs: device tags are set via BulkEditDialog and
 *     `useScreenActions`. Feeds keep theirs: OptiSigns curates them.
 * Feeds still end at Tags: they're OptiSigns-curated templates with no folder
 * location and no actionable freshness date (see `getAllViewRecency`).
 */
export const ENTITY_COLUMNS: Record<SearchEntityType, readonly SearchColumn[]> = {
  screens: [STATUS, TAGS, LOCATION, LAST_ONLINE],
  assets: [TAGS, LOCATION, LAST_UPDATED],
  engages: [TAGS, LOCATION, LAST_UPDATED],
  playlists: [LOCATION, LAST_UPDATED],
  schedules: [LOCATION, LAST_UPDATED],
  operationalSchedules: [LOCATION, LAST_UPDATED],
  folders: [LOCATION, LAST_UPDATED],
  feeds: [STATUS, TAGS],
  teams: [MEMBERS, LAST_UPDATED],
  members: [ROLE, EMAIL, TEAM, LAST_UPDATED],
};

/**
 * Entity types that have a real OWNING teamspace worth naming in a column.
 *
 * The same set `SearchPage` uses for its shared-with-me glyph, and for the same reason: a member
 * row's `teamId` is the team the MEMBERSHIP belongs to, a team row's `teamId` is itself, and feeds
 * have none — so a teamspace column on those would state something false. Members already carry a
 * dedicated `team` column, which is that section's honest answer to the same question.
 */
const WORKSPACE_COLUMN_TYPES = new Set<SearchEntityType>([
  'screens',
  'assets',
  'engages',
  'playlists',
  'schedules',
  'operationalSchedules',
  'folders',
]);

/**
 * The trailing columns for one section, with the All-view Teamspace column spliced in when the
 * scope calls for it.
 *
 * A function rather than a second static record because the column set is now scope-dependent, and
 * because the header and the rows MUST agree: both call this with the same `showWorkspace`, so they
 * cannot drift the way the Screens page's three hand-synced files do (#1897, the bug this file's
 * header comment is about).
 *
 * Inserted immediately BEFORE `location`, matching the Screens list's own column order, so
 * "which teamspace / which folder / when" reads as one group.
 */
export function getEntityColumns(
  entityType: SearchEntityType,
  showWorkspace: boolean,
): readonly SearchColumn[] {
  const columns = ENTITY_COLUMNS[entityType];
  if (!showWorkspace || !WORKSPACE_COLUMN_TYPES.has(entityType)) return columns;
  const at = columns.findIndex((column) => column.id === 'location');
  if (at < 0) return [...columns, WORKSPACE];
  return [...columns.slice(0, at), WORKSPACE, ...columns.slice(at)];
}

/**
 * Path of the folder a result LIVES IN, for the Location column's
 * `LocationCell` (which derives the leaf name + navigation target from it).
 *
 * Two wire shapes feed this:
 *   - `path` is already the parent path — screens, assets, engages, folders
 *   - `parentGroupInfo` names the containing folder, so its own `path` must
 *     be joined with its `name` to get the full path — playlists, schedules,
 *     operational schedules
 * Returns `null` for entities with no folder context (feeds, teams,
 * members), which is how the row knows to render an empty cell.
 */
export function getContainerPath(
  entityType: SearchEntityType,
  item: Record<string, unknown>,
): string | null {
  if (
    entityType === 'screens' ||
    entityType === 'assets' ||
    entityType === 'engages' ||
    entityType === 'folders'
  ) {
    return (item.path as string | null | undefined) ?? '';
  }
  if (
    entityType === 'playlists' ||
    entityType === 'schedules' ||
    entityType === 'operationalSchedules'
  ) {
    const parent = item.parentGroupInfo as { name?: string; path?: string | null } | undefined;
    if (!parent?.name) return '';
    const above = parent.path && parent.path !== '/' ? parent.path : '';
    return `${above}/${parent.name}`;
  }
  return null;
}

/**
 * Which section a folder belongs to, keyed by its `type` (a `GroupType` value).
 * Lets the folder row name its home section using the SAME label the filter
 * pills and section titles use, instead of inventing a second vocabulary.
 */
const FOLDER_TYPE_TO_SECTION: Record<string, SearchEntityType> = {
  device: 'screens',
  asset: 'assets',
  playlist: 'playlists',
  schedule: 'schedules',
  engage: 'engages',
};

/**
 * Where a folder LIVES, as a plain-text trail: "Files/Assets", or
 * "Files/Assets › Marketing" when it sits inside another folder.
 *
 * Replaces the folder Type column, which rendered "Asset Folder" as a badge —
 * it looked like a tag, and a badge saying which KIND of folder this is never
 * answered the question a reader actually has, which is where it sits. The
 * trail answers both at once: the first segment IS the kind.
 *
 * `sectionLabel` is injected so this file stays free of i18n imports.
 */
export function getFolderTrailSegments(
  item: Record<string, unknown>,
  sectionLabel: (entityType: SearchEntityType) => string,
): string[] {
  const type = (item.type as string | undefined)?.toLowerCase();
  const section = type ? FOLDER_TYPE_TO_SECTION[type] : undefined;
  const path = ((item.path as string | null | undefined) ?? '')
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
  return [section ? sectionLabel(section) : null, ...path].filter(Boolean) as string[];
}

/** Joined form, for the results page's Location column. */
export function getFolderTrail(
  item: Record<string, unknown>,
  sectionLabel: (entityType: SearchEntityType) => string,
): string {
  const type = (item.type as string | undefined)?.toLowerCase();
  const section = type ? FOLDER_TYPE_TO_SECTION[type] : undefined;
  const segments = ((item.path as string | null | undefined) ?? '')
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
  return [section ? sectionLabel(section) : null, ...segments].filter(Boolean).join(' › ');
}
